import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { sessions } from '../lib/api';
import Layout from '../components/Layout';

export default function Sessions() {
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');

  const { data: userSessions, isLoading } = useQuery({
    queryKey: ['sessions'],
    queryFn: sessions.list,
  });

  const filteredSessions = userSessions?.filter(session => {
    const matchesStatus = statusFilter === 'all' || session.status === statusFilter;
    const matchesSearch = !searchQuery || 
      session.initialIdea?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesStatus && matchesSearch;
  }) || [];

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      in_progress: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      completed: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      paused: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
      cancelled: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
    };
    const labels: Record<string, string> = {
      in_progress: '進行中',
      completed: '完了',
      paused: '一時停止',
      cancelled: 'キャンセル',
    };
    return (
      <span className={`px-2 py-1 text-xs rounded-full ${styles[status] || styles.cancelled}`}>
        {labels[status] || status}
      </span>
    );
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* ヘッダー */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
              セッション一覧
            </h1>
            <p style={{ color: 'var(--text-secondary)' }}>
              過去のヒアリングセッションを確認できます
            </p>
          </div>
          <Link to="/" className="btn-primary inline-flex items-center gap-2">
            💬 新規ヒアリング
          </Link>
        </div>

        {/* フィルター */}
        <div className="card">
          <div className="flex flex-col md:flex-row gap-4">
            {/* 検索 */}
            <div className="flex-1 relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                🔍
              </span>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="プロジェクト名で検索..."
                className="input pl-10"
              />
            </div>

            {/* ステータスフィルター */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="input w-full md:w-48"
            >
              <option value="all">すべてのステータス</option>
              <option value="in_progress">進行中</option>
              <option value="completed">完了</option>
              <option value="paused">一時停止</option>
              <option value="cancelled">キャンセル</option>
            </select>
          </div>
        </div>

        {/* セッション一覧 */}
        <div className="card">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin w-8 h-8 border-4 border-jarvis-gold border-t-transparent rounded-full" />
            </div>
          ) : filteredSessions.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-4xl mb-2">💭</p>
              <p style={{ color: 'var(--text-secondary)' }}>
                {searchQuery || statusFilter !== 'all'
                  ? '条件に一致するセッションがありません'
                  : 'まだセッションがありません'}
              </p>
              {!searchQuery && statusFilter === 'all' && (
                <Link to="/" className="btn-outline mt-4 inline-block">
                  最初のヒアリングを開始
                </Link>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b" style={{ borderColor: 'var(--border-color)' }}>
                    <th className="text-left py-3 px-4 text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
                      プロジェクト
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
                      進捗
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
                      ステータス
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
                      作成日
                    </th>
                    <th className="text-right py-3 px-4 text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
                      アクション
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSessions.map((session) => (
                    <tr
                      key={session.id}
                      className="border-b hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                      style={{ borderColor: 'var(--border-color)' }}
                    >
                      <td className="py-3 px-4">
                        <p className="font-medium" style={{ color: 'var(--text-primary)' }}>
                          {session.initialIdea?.substring(0, 50) || '（未入力）'}
                          {session.initialIdea && session.initialIdea.length > 50 ? '...' : ''}
                        </p>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <div className="w-24 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-jarvis-gold transition-all"
                              style={{ width: `${(session.answeredQuestions / session.totalQuestions) * 100}%` }}
                            />
                          </div>
                          <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                            {session.answeredQuestions}/{session.totalQuestions}
                          </span>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        {getStatusBadge(session.status)}
                      </td>
                      <td className="py-3 px-4 text-sm" style={{ color: 'var(--text-secondary)' }}>
                        {new Date(session.createdAt).toLocaleDateString('ja-JP')}
                      </td>
                      <td className="py-3 px-4 text-right space-x-2">
                        <Link
                          to={`/sessions/${session.id}`}
                          className="text-sm text-jarvis-gold hover:underline"
                        >
                          詳細
                        </Link>
                        {session.status === 'paused' && (
                          <Link
                            to={`/sessions/${session.id}/resume`}
                            className="text-sm text-blue-500 hover:underline"
                          >
                            再開
                          </Link>
                        )}
                        {session.status === 'completed' && (
                          <Link
                            to={`/sessions/${session.id}/blueprint`}
                            className="text-sm text-green-500 hover:underline"
                          >
                            設計書
                          </Link>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}

